import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, Folder } from 'lucide-react';
import { SubFolderSummary } from '../../../services/api/folderService';
import { stripHtml } from '../descriptionUtils';
import { FolderSection } from './FolderSection';
import { SectionRows } from './SectionRows';
import { folderItemCount } from '../folderCounts';

interface SubFolderListProps {
    subFolders: SubFolderSummary[];
    onCreate?: () => void;
}

export const SubFolderList = ({ subFolders, onCreate }: SubFolderListProps) => {
    const { t } = useTranslation();

    return (
        <FolderSection
            title={t('folders.detail.subFolders')}
            count={subFolders.length}
            action={
                onCreate
                    ? {
                          label: t('folders.detail.newSubFolder'),
                          onClick: onCreate,
                      }
                    : undefined
            }
        >
            <SectionRows>
                {subFolders.map((sub) => {
                    const description = stripHtml(sub.description);
                    const itemCount = folderItemCount(sub);
                    return (
                        <Link
                            key={sub._id}
                            to={`/group/${sub._id}`}
                            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted"
                        >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-subtle bg-muted">
                                <Folder
                                    aria-hidden="true"
                                    className="h-4 w-4 text-subtle-foreground"
                                />
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block break-words text-sm font-semibold text-foreground">
                                    {sub.title}
                                </span>
                                {description && (
                                    <span className="block truncate text-xs font-medium text-subtle-foreground">
                                        {description}
                                    </span>
                                )}
                            </span>
                            <span className="shrink-0 text-xs font-medium text-subtle-foreground">
                                {itemCount > 0
                                    ? t('folders.overview.counts.items', {
                                          count: itemCount,
                                      })
                                    : t('folders.overview.counts.empty')}
                            </span>
                            <ChevronRight
                                aria-hidden="true"
                                className="h-4 w-4 shrink-0 text-subtle-foreground"
                            />
                        </Link>
                    );
                })}
            </SectionRows>
        </FolderSection>
    );
};
